/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : aircraftDetailsSlice.ts
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Redux Slice for Aircraft Details state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Jan 10,2022
 *
 * @Version      : Version 1.0
 *
 * By            :
 * On            :
 * Description   :
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IAircraftDetailsState,
  IAircraftDetailsFormFields,
  ISavedNewAPMFileDetails,
  IAircraftField,
  IAircraftDetailsField,
} from "models/aircraftDetails";
import { AppThunk, RootState } from "stores/GlobalStore";
import { setSelectedApmRecordData } from "stores/ApmWorkFlowStore";
import { apiCallBegan, apiVersion, apmURLs } from "api";

export const initialState: IAircraftDetailsState = {
  aircraftDetailsMasterData: null,
  aircraftDetailsMasterLoader: false,
  selectedaircraftDetails: {
    make: "",
    model: "",
    tailNumber: "",
    serialNumber: "",
  },
  saveAircraftDetailsLoader: false,
  aircraftDetailsData: null,
  SavedAPMFileRecord: null,
};

const aircraftDetailsSlice = createSlice({
  name: "aircraftDetails",
  initialState,
  reducers: {
    setAircraftDetailsMasterData(
      aircraftDetailsState: IAircraftDetailsState,
      action: PayloadAction<IAircraftField>
    ) {
      aircraftDetailsState.aircraftDetailsMasterData = action.payload.aircrafts;
      aircraftDetailsState.aircraftDetailsMasterLoader = false;
    },
    beginAircraftDetailsMasterLoader(
      aircraftDetailsState: IAircraftDetailsState
    ) {
      aircraftDetailsState.aircraftDetailsMasterLoader = true;
    },
    beginSaveDetailsLoader(aircraftDetailsState: IAircraftDetailsState) {
      aircraftDetailsState.saveAircraftDetailsLoader = true;
    },
    resetAircraftDetailsMasterDataOnError(
      aircraftDetailsState: IAircraftDetailsState
    ) {
      aircraftDetailsState.aircraftDetailsMasterData = null;
      aircraftDetailsState.aircraftDetailsMasterLoader = false;
    },
    setSelectedAircraftDetails(
      aircraftDetailsState: IAircraftDetailsState,
      action: PayloadAction<IAircraftDetailsFormFields>
    ) {
      aircraftDetailsState.selectedaircraftDetails = action.payload;
    },
    setSavedDetailsData(aircraftDetailsState: IAircraftDetailsState, action) {
      aircraftDetailsState.saveAircraftDetailsLoader = false;
      aircraftDetailsState.SavedAPMFileRecord = action.payload;
    },
    setNextDetailsData(
      aircraftDetailsState: IAircraftDetailsState,
      action: PayloadAction<ISavedNewAPMFileDetails>
    ) {
      aircraftDetailsState.saveAircraftDetailsLoader = false;
      aircraftDetailsState.SavedAPMFileRecord = action.payload;
    },
    SaveDetailsLoaderError(aircraftDetailsState: IAircraftDetailsState) {
      aircraftDetailsState.saveAircraftDetailsLoader = false;
    },
    resetSelectedAircraftDetails(aircraftDetailsState: IAircraftDetailsState) {
      aircraftDetailsState.selectedaircraftDetails =
        initialState.selectedaircraftDetails;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      setSelectedApmRecordData,
      (aircraftDetailsState: IAircraftDetailsState, action) => {
        aircraftDetailsState.selectedaircraftDetails.make = action.payload.make;
        aircraftDetailsState.selectedaircraftDetails.model =
          action.payload.model;
        aircraftDetailsState.selectedaircraftDetails.serialNumber =
          action.payload.serialNumber;
        aircraftDetailsState.selectedaircraftDetails.tailNumber =
          action.payload.tailNumber;
      }
    );
  },
});

const {
  setAircraftDetailsMasterData,
  beginAircraftDetailsMasterLoader,
  resetAircraftDetailsMasterDataOnError,
  beginSaveDetailsLoader,
  SaveDetailsLoaderError,
} = aircraftDetailsSlice.actions;

export const {
  setSelectedAircraftDetails,
  setNextDetailsData,
  setSavedDetailsData,
  resetSelectedAircraftDetails,
} = aircraftDetailsSlice.actions;

export const aircraftDetailsReducer = aircraftDetailsSlice.reducer;

export const fetchAircraftDetailsMasterData =
  (): AppThunk => async (dispatch) => {
    const url = apiVersion.v1 + apmURLs.AIRCRAFT_DETAILS_MASTER_DATA;

    dispatch(
      apiCallBegan({
        url,
        onStart: beginAircraftDetailsMasterLoader.type,
        onSuccess: setAircraftDetailsMasterData.type,
        onError: resetAircraftDetailsMasterDataOnError.type,
        showGlobalError: true,
      })
    );
  };

export const SaveAircraftDetails =
  (aircraftDetails: IAircraftDetailsFormFields, type: string): AppThunk =>
  async (dispatch, getState) => {
    const generalInfoValues = getState().generalInfo.formFieldsData;
    const selectedApmFile = getState().selectApmFile.selectedApmFile;
    const selectedCustomer = getState().customer.selectedCustomer;
    const currentApmFileRecord = getState().apmWorkFlow.currentApmFileRecord;
    const { customerEmail, customerPhone } = generalInfoValues;
    const url = apiVersion.v1 + apmURLs.AIRCRAFT_DETAILS_MASTER_DATA;

    const params = {
      partNumber: selectedApmFile?.partNumber,
    };

    const data = {
      baseFileName: selectedApmFile ? selectedApmFile.partNumber : null,
      partNumber: null,
      customerEmail: customerEmail,
      customerPhone: customerPhone,
      customerId: selectedCustomer?.customerId.toString(),
      customerName: selectedCustomer?.customerName,
      ...aircraftDetails,
      status: null,
      previousScreen: "3",
      historyId: currentApmFileRecord ? currentApmFileRecord?.historyId : null,
      lsapFileName: selectedApmFile ? selectedApmFile.lsapFileName : null,
    };

    dispatch(
      apiCallBegan({
        url,
        params,
        data,
        method: "POST",
        onStart: beginSaveDetailsLoader.type,
        onSuccess:
          type === "save" ? setSavedDetailsData.type : setNextDetailsData.type,
        onError: SaveDetailsLoaderError.type,
        showGlobalError: true,
      })
    );
  };

export const selectAircraftDetailsMasterData = (
  state: RootState
): IAircraftDetailsField | null =>
  state.aircraftDetails.aircraftDetailsMasterData;
export const selectAircraftDetailsMasterLoader = (state: RootState): boolean =>
  state.aircraftDetails.aircraftDetailsMasterLoader;
export const selectSaveDetailsLoader = (state: RootState): boolean =>
  state.aircraftDetails.saveAircraftDetailsLoader;
export const selectSelectedAircraftDetails = (
  state: RootState
): IAircraftDetailsFormFields => state.aircraftDetails.selectedaircraftDetails;
